import { useState } from 'react';
import { suggestPassage, type PassageFix } from '../api';
import { markFixed } from '../account';
import Info from './Glossary';
import { hostOf, externalHref } from '../lib/url';

interface Brand { name: string; domain: string; aliases?: string[] }

interface Loss {
  url: string;
  /** Questions this page was cited for and answered without naming the brand. */
  questions: { query: string; rivals: string[] }[];
  /** Every brand named instead, across those questions. */
  rivals: string[];
}

/**
 * Split the `cited_only` losses into the two that behave completely differently,
 * and name the exact page behind each one.
 *
 * `cited_only` says the AI had your page and answered without you. What it does
 * NOT say is whether an edit would change that — and the answer is in data we
 * already store per question:
 *
 *  - **rivals were named** → the model does write brand names for this question,
 *    just not yours. That is a page problem, and the page is knowable: it is the
 *    one of your URLs that appeared in the citations.
 *  - **nobody was named** → the model answers this class of question with generic
 *    prose and puts sources underneath. No wording on your page wins that, and
 *    saying otherwise would sell a fix that cannot work.
 */
/**
 * Only the fields the split needs, and `state` stays a plain string — a stored
 * run is JSON from the database, not a value we can promise is one of three
 * literals. Anything that is not `cited_only` is simply skipped.
 */
export type ProbeLike = { query: string; state: string; competitors?: string[]; citations?: string[] };

export function lossesByPage(probes: ProbeLike[], domain: string): { fixable: Loss[]; unwinnable: number } {
  const byUrl = new Map<string, Loss>();
  let unwinnable = 0;

  for (const p of probes) {
    if (p.state !== 'cited_only') continue;
    const rivals = p.competitors ?? [];
    if (rivals.length === 0) { unwinnable++; continue; }
    const own = (p.citations ?? []).filter((c) => {
      const h = hostOf(c);
      return h === domain || h.endsWith(`.${domain}`);
    });
    for (const url of new Set(own)) {
      const at = byUrl.get(url) ?? { url, questions: [], rivals: [] };
      at.questions.push({ query: p.query, rivals });
      at.rivals = [...new Set([...at.rivals, ...rivals])];
      byUrl.set(url, at);
    }
  }
  const fixable = [...byUrl.values()].sort((a, b) => b.questions.length - a.questions.length);
  return { fixable, unwinnable };
}

function PageCard({ loss, brand, lang }: { loss: Loss; brand: Brand; lang: string | null }): JSX.Element {
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [fix, setFix] = useState<PassageFix | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  /** Questions the client says they have acted on — the next run reports back. */
  const [done, setDone] = useState<Set<string>>(() => new Set());
  const [marking, setMarking] = useState<string | null>(null);

  async function ask(q: { query: string; rivals: string[] }): Promise<void> {
    setOpenFor(q.query); setFix(null); setErr(null); setBusy(true);
    try {
      setFix(await suggestPassage({
        pageUrl: loss.url, question: q.query, brandName: brand.name,
        aliases: brand.aliases, rivals: q.rivals, lang,
      }));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'could not read that page');
    } finally {
      setBusy(false);
    }
  }

  async function markDone(q: { query: string; rivals: string[] }): Promise<void> {
    setMarking(q.query);
    try {
      await markFixed({ domain: brand.domain, query: q.query, pageUrl: loss.url, stateBefore: 'cited_only' });
      setDone((prev) => new Set(prev).add(q.query));
    } catch { /* the mark is a convenience; failing to record it must not block the page */ }
    finally { setMarking(null); }
  }

  function copy(text: string): void {
    void navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }

  return (
    <div className="border border-estimated/40 bg-estimated/5 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <a href={externalHref(loss.url)} target="_blank" rel="noreferrer" className="text-cyan hover:underline break-all text-sm flex-1 min-w-[14rem]">
          {loss.url}
        </a>
        <span className="font-mono text-xs text-estimated shrink-0">
          {loss.questions.length} {loss.questions.length === 1 ? 'question' : 'questions'} lost here
        </span>
      </div>
      <p className="text-sm text-text-secondary mt-1">
        AI read this page, then named{' '}
        <span className="text-[#E0574A]">{loss.rivals.slice(0, 4).join(', ')}</span>
        {loss.rivals.length > 4 && ` and ${loss.rivals.length - 4} more`} instead of you.
      </p>

      <div className="flex flex-col gap-1.5 mt-3">
        {loss.questions.map((q) => (
          <div key={q.query} className="border border-warm-dark-2 rounded-lg px-3 py-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-text-primary flex-1 min-w-[12rem]">“{q.query}”</p>
              <span className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => void ask(q)}
                  disabled={busy}
                  className="font-mono text-xs text-cyan border border-cyan/40 rounded px-2 py-1 hover:bg-cyan/10 disabled:opacity-40 transition"
                >
                  {busy && openFor === q.query ? 'reading the page…' : 'Show me the text to change →'}
                </button>
                {/* Closes the loop: the next run of this question reports whether
                    the change worked, instead of the advice ending here. */}
                <button
                  onClick={() => void markDone(q)}
                  disabled={marking === q.query || done.has(q.query)}
                  title={done.has(q.query)
                    ? 'Recorded. The next run of this question will say whether it moved.'
                    : 'Say you have changed this page and the next run will report back on it'}
                  className={`font-mono text-xs rounded px-2 py-1 border transition ${
                    done.has(q.query)
                      ? 'text-success border-success/50 bg-success/10'
                      : 'text-text-secondary border-[#70707D] hover:text-cyan hover:border-cyan/60'
                  }`}
                >
                  {done.has(q.query) ? 'marked ✓' : marking === q.query ? '…' : 'I fixed this'}
                </button>
              </span>
            </div>

            {openFor === q.query && err && (
              <p className="font-mono text-sm text-[#E0574A] border border-[#E0574A]/40 rounded-lg px-3 py-2 mt-2">{err}</p>
            )}

            {openFor === q.query && fix && (
              <div className="mt-2">
                {fix.verdict === 'rewrite' && (
                  <>
                    <p className="font-mono text-xs text-text-secondary mb-1">On your page today</p>
                    <p className="text-sm text-text-secondary border-l-2 border-warm-dark-2 pl-3">{fix.passage}</p>
                    <div className="flex items-center gap-2 mt-3 mb-1">
                      <p className="font-mono text-xs text-cyan">Change it to</p>
                      <button onClick={() => copy(fix.suggestion)} className="font-mono text-xs text-text-secondary hover:text-cyan transition">
                        {copied ? 'copied ✓' : 'copy'}
                      </button>
                    </div>
                    <p className="text-sm text-text-primary border-l-2 border-cyan pl-3">{fix.suggestion}</p>
                    <p className="text-sm text-text-secondary mt-2">
                      {fix.why} <span className="text-estimated">Read it before you publish it</span>. This only moved
                      your name into the sentence, and you know your copy better than a draft does.
                    </p>
                  </>
                )}
                {fix.verdict === 'named' && (
                  <>
                    <p className="text-sm text-text-primary">This passage already names you.</p>
                    <p className="text-sm text-text-secondary border-l-2 border-warm-dark-2 pl-3 mt-1">{fix.passage}</p>
                    <p className="text-sm text-text-secondary mt-2">
                      {fix.why} So the wording is not what is costing you this question. Look at whether the answer is
                      buried far down the page, or whether the page has no <span className="font-mono">Organization</span> schema
                      tying the name to it.
                    </p>
                  </>
                )}
                {fix.verdict === 'no_answer' && (
                  <div className="text-sm text-text-secondary mt-1">
                    <p>
                      <span className="text-text-primary">This page has no passage that answers that question.</span> {fix.why}
                    </p>
                    <p className="mt-2">
                      <span className="text-text-primary">What to do:</span> add a short section to this page that answers
                      “{q.query}” directly, under a heading close to the question, with your name in its first sentence. If
                      the question belongs on another page of yours, answer it there and link to it from here. Working your
                      name into an unrelated paragraph would not make AI name you.
                    </p>
                  </div>
                )}
                {/* Whatever the verdict, the page itself is one click away: the
                    reader should judge the advice against the real page. */}
                <a href={externalHref(loss.url)} target="_blank" rel="noreferrer"
                  className="inline-block font-mono text-xs text-cyan border border-cyan/40 rounded px-2 py-1 mt-3 hover:bg-cyan/10 transition">
                  Open the page ↗
                </a>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * "Which of my pages did AI read and then answer without me — and what exactly
 * do I change?"
 */
export default function ClaimYourPages({ probes, brand, lang, isPro }: {
  probes: ProbeLike[];
  brand: Brand;
  lang: string | null;
  isPro: boolean;
}): JSX.Element | null {
  const { fixable, unwinnable } = lossesByPage(probes, brand.domain);
  if (fixable.length === 0 && unwinnable === 0) return null;

  return (
    <div className="w-full border border-warm-dark-2 rounded-xl p-5 mt-5">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="font-serif text-xl">Pages AI read, then answered without you</h3>
        <Info term="cited_only" />
      </div>
      <p className="text-sm text-text-secondary mb-4">
        These are the cheapest wins in the whole report. AI already finds and reads these pages. It just wrote the
        answer without your name in it. That is a wording problem on a page you control, not a months-long off-site one.
      </p>

      {fixable.length > 0 ? (
        <div className="flex flex-col gap-3">
          {isPro
            ? fixable.map((l) => <PageCard key={l.url} loss={l} brand={brand} lang={lang} />)
            : fixable.map((l) => (
              <div key={l.url} className="border border-estimated/40 bg-estimated/5 rounded-xl p-4">
                <a href={externalHref(l.url)} target="_blank" rel="noreferrer" className="text-cyan hover:underline break-all text-sm">{l.url}</a>
                <p className="text-sm text-text-secondary mt-1">
                  {l.questions.length} {l.questions.length === 1 ? 'question' : 'questions'} lost here, and AI named{' '}
                  <span className="text-[#E0574A]">{l.rivals.slice(0, 3).join(', ')}</span> instead.
                </p>
                <p className="text-sm text-text-secondary mt-2">
                  <span className="text-estimated">Pro</span> finds the exact sentence on this page and shows it
                  rewritten so your name survives the paraphrase.
                </p>
              </div>
            ))}
        </div>
      ) : (
        <p className="text-sm text-text-secondary">
          None of these losses have a competitor named alongside them. See below.
        </p>
      )}

      {/* The honest half. Saying "edit your page" here would sell a fix that
          cannot work, and that is the one thing this product must not do. */}
      {unwinnable > 0 && (
        <div className="mt-4 pt-3 border-t border-warm-dark-2">
          <p className="text-sm text-text-primary">
            {unwinnable} more {unwinnable === 1 ? 'question was' : 'questions were'} answered from your pages with{' '}
            <span className="text-text-primary">no brand named at all</span>. Not yours, not anyone&apos;s.
          </p>
          <p className="text-sm text-text-secondary mt-1">
            For those, AI writes a generic explanation and lists sources underneath. No wording on your page changes
            that, so this will not tell you to rewrite anything. Spend the effort on the pages above instead.
          </p>
        </div>
      )}
    </div>
  );
}
